import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../../components/ToastProvider';
import ConfirmDialog from '../../components/ConfirmDialog';

const IngredientManagement = () => {
    const { restaurant } = useSelector(state => state.auth);
    const restaurantId = restaurant?._id;
    const addToast = useToast();
    const [ingredients, setIngredients] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, ingredientId: null });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        unit: 'kg',
        purchasePrice: '',
        currentStock: '',
        reorderLevel: '',
        supplier: ''
    });

    const units = ['kg', 'g', 'ml', 'l', 'pcs', 'cups', 'tbsp', 'tsp'];

    useEffect(() => {
        fetchIngredients();
    }, []);

    const fetchIngredients = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_INGREDIENTS}?restaurantId=${restaurantId}`);
            setIngredients(response.data);
        } catch (error) {
            console.error('Error fetching ingredients:', error);
            addToast('Failed to fetch ingredients', 'error');
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchIngredients();
            addToast('Data refreshed successfully!', 'success');
        } catch (error) {
            console.error('Error refreshing data:', error);
            addToast('Failed to refresh data', 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataWithRestaurantId = { ...formData, restaurantId };
            if (editingId) {
                await axios.put(API_ENDPOINTS.INVENTORY_INGREDIENT(editingId), dataWithRestaurantId);
                addToast('Ingredient updated successfully!', 'success');
            } else {
                await axios.post(API_ENDPOINTS.INVENTORY_INGREDIENTS, dataWithRestaurantId);
                addToast('Ingredient created successfully!', 'success');
            }
            fetchIngredients();
            resetForm();
        } catch (error) {
            console.error('Error saving ingredient:', error);
            addToast('Failed to save ingredient', 'error');
        }
    };

    const handleEdit = (ingredient) => {
        setFormData(ingredient);
        setEditingId(ingredient._id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(API_ENDPOINTS.INVENTORY_INGREDIENT(id));
            addToast('Ingredient deleted successfully!', 'success');
            fetchIngredients();
        } catch (error) {
            console.error('Error deleting ingredient:', error);
            addToast('Failed to delete ingredient', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            unit: 'kg',
            purchasePrice: '',
            currentStock: '',
            reorderLevel: '',
            supplier: ''
        });
        setEditingId(null);
        setShowForm(false);
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800">Ingredients</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {isRefreshing ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    >
                        Add Ingredient
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="bg-white p-3 sm:p-4 rounded-xl border border-stone-200 shadow-sm mb-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-2">
                        {editingId ? 'Edit Ingredient' : 'Add New Ingredient'}
                    </p>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                        <input
                            type="text"
                            placeholder="Ingredient name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            required
                        />
                        <select
                            value={formData.unit}
                            onChange={(e) => setFormData({...formData, unit: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white"
                        >
                            {units.map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                            ))}
                        </select>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Purchase price (per unit)"
                            value={formData.purchasePrice}
                            onChange={(e) => setFormData({...formData, purchasePrice: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            required
                        />
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Current stock"
                            value={formData.currentStock}
                            onChange={(e) => setFormData({...formData, currentStock: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            required
                        />
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Reorder level"
                            value={formData.reorderLevel}
                            onChange={(e) => setFormData({...formData, reorderLevel: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Supplier (optional)"
                            value={formData.supplier}
                            onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                            className="px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                        />
                        <div className="col-span-1 md:col-span-2 flex gap-2">
                            <button
                                type="submit"
                                className="px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                            >
                                {editingId ? 'Update' : 'Add'} Ingredient
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-xs">
                        <thead className="bg-stone-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Name</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Unit</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Price</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Stock</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600 hidden md:table-cell">Reorder</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Status</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ingredients.map((ingredient) => (
                                <tr key={ingredient._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                    <td className="px-3 py-2 font-medium text-stone-800">{ingredient.name}</td>
                                    <td className="px-3 py-2 text-stone-600">{ingredient.unit}</td>
                                    <td className="px-3 py-2 tabular-nums">₹{ingredient.purchasePrice?.toFixed(2)}</td>
                                    <td className="px-3 py-2 tabular-nums">{ingredient.currentStock?.toFixed(2)}</td>
                                    <td className="px-3 py-2 tabular-nums hidden md:table-cell">{ingredient.reorderLevel?.toFixed(2)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            ingredient.currentStock <= ingredient.reorderLevel
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                            {ingredient.currentStock <= ingredient.reorderLevel ? 'Low' : 'OK'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleEdit(ingredient)}
                                                className="text-orange-600 hover:text-orange-700 font-medium"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmDialog({ isOpen: true, ingredientId: ingredient._id })}
                                                className="text-red-600 hover:text-red-700 font-medium"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ isOpen: false, ingredientId: null })}
                onConfirm={() => handleDelete(confirmDialog.ingredientId)}
                title="Delete Ingredient"
                message="Are you sure you want to delete this ingredient? This action cannot be undone."
            />
        </div>
    );
};

export default IngredientManagement;